import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, computeDueStatus } from "@/lib/api";
import { Link } from "react-router-dom";
import StatusPill from "@/components/StatusPill";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Search, Eye, Pencil, Archive, Receipt, Upload, Banknote, Lock, Printer, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { CARD_KINDS } from "@/lib/api";
import StatementPasswordReveal from "@/components/StatementPasswordReveal";
import { Sensitive } from "@/components/security/Sensitive";
import EmptyState from "@/components/EmptyState";

// Per-kind visual tag — helps admin instantly spot Credit vs Debit vs Forex.
const KIND_STYLES = {
    "Credit Card":          "bg-blue-50 text-blue-800 border-blue-200",
    "Debit Card":           "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Charge Card":          "bg-stone-900 text-white border-stone-900",
    "Forex Card":           "bg-purple-50 text-purple-800 border-purple-200",
    "Prepaid Card":         "bg-amber-50 text-amber-800 border-amber-200",
    "Virtual Card":         "bg-teal-50 text-teal-800 border-teal-200",
    "Corporate Card":       "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Fuel Card":            "bg-orange-50 text-orange-800 border-orange-200",
    "Business Credit Card": "bg-slate-50 text-slate-800 border-slate-200",
    "BNPL":                 "bg-rose-50 text-rose-800 border-rose-200",
};
const KIND_SHORT = {
    "Credit Card": "Credit",  "Debit Card": "Debit",  "Charge Card": "Charge",
    "Forex Card":  "Forex",   "Prepaid Card": "Prepaid", "Virtual Card": "Virtual",
    "Corporate Card": "Corporate", "Fuel Card": "Fuel", "Business Credit Card": "Business",
    "BNPL": "BNPL",
};
const kindPillClass = (k) => KIND_STYLES[k] || KIND_STYLES["Credit Card"];
const shortKind     = (k) => KIND_SHORT[k]  || (k || "Credit");

export default function Cards() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [kind, setKind] = useState("all");
    const [outstanding, setOutstanding] = useState({});
    const [scores, setScores] = useState({});
    const [rewardValue, setRewardValue] = useState({});

    const load = () => {
        api.get("/cards").then((r) => setCards(r.data));
        api.get("/dashboard/summary").then((r) => setOutstanding(r.data.outstanding_by_card || {}));
        api.get("/analytics/card-scores").then((r) => {
            const map = {};
            (r.data.cards || []).forEach((s) => { map[s.card_id] = s; });
            setScores(map);
        }).catch((e) => console.error("[Cards] scores failed", e));
        api.get("/analytics/reward-value").then((r) => {
            const map = {};
            (r.data.rows || []).forEach((row) => { map[row.card_id] = row; });
            setRewardValue(map);
        }).catch((e) => console.error("[Cards] reward-value failed", e));
    };
    useEffect(() => { load(); }, []);

    const archive = async (id, label) => {
        if (!window.confirm(`Archive card ${label}? History is preserved but no new expenses allowed.`)) return;
        try {
            await api.delete(`/cards/${id}`);
            toast.success("Card archived");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to archive");
        }
    };

    /** Generate a one-page landscape PDF summary of every card matching the
     *  current filter — for admin/finance to print or share with an accountant. */
    const printCards = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const w = doc.internal.pageSize.getWidth();
        const now = new Date().toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });

        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text("VAULT · Credit Card Register", 40, 42);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(`${filtered.length} of ${cards.length} cards · Printed ${now}`, 40, 58);
        doc.text("Confidential · card numbers masked", w - 40, 58, { align: "right" });
        doc.setDrawColor(200);
        doc.line(40, 66, w - 40, 66);

        // Table header
        const cols = [
            { key: "bank_name",     label: "Bank",     x: 40,  align: "left"  },
            { key: "card_name",     label: "Card",     x: 130, align: "left"  },
            { key: "kind",          label: "Type",     x: 240, align: "left"  },
            { key: "level",         label: "Level",    x: 305, align: "left"  },
            { key: "last_4",        label: "Last 4",   x: 375, align: "left"  },
            { key: "holder",        label: "Holder",   x: 420, align: "left"  },
            { key: "limit",         label: "Limit",    x: 555, align: "right" },
            { key: "outstanding",   label: "Outstd.",  x: 615, align: "right" },
            { key: "due",           label: "Due",      x: 660, align: "right" },
            { key: "status",        label: "Status",   x: 690, align: "left"  },
            { key: "utilization",   label: "Util %",   x: 800, align: "right" },
        ];
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(80);
        cols.forEach((c) => doc.text(c.label, c.x, 82, { align: c.align }));
        doc.setDrawColor(220);
        doc.line(40, 88, w - 40, 88);

        // Rows
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30);
        const rowH = 14;
        let y = 100;
        filtered.forEach((c, idx) => {
            if (y > 555) { doc.addPage(); y = 60; }
            if (idx % 2 === 0) {
                doc.setFillColor(248, 247, 245);
                doc.rect(40, y - 10, w - 80, rowH, "F");
            }
            const os = outstanding[c.id] || 0;
            const s = scores[c.id] || {};
            const util = s.utilization_pct != null ? `${s.utilization_pct}%` : "—";
            const rowData = {
                bank_name:   (c.bank_name || "—").slice(0, 16),
                card_name:   (c.card_name || "—").slice(0, 20),
                kind:        shortKind(c.card_kind).slice(0, 11),
                level:       (c.card_level || "—").slice(0, 12),
                last_4:      `••${c.last_4 || "—"}`,
                holder:      (c.card_holder_name || "—").slice(0, 22),
                limit:       c.credit_limit ? `₹${Number(c.credit_limit).toLocaleString("en-IN")}` : "—",
                outstanding: os ? `₹${Number(os).toLocaleString("en-IN")}` : "—",
                due:         c.payment_due_day ? `${c.payment_due_day}` : "—",
                status:      c.status || "—",
                utilization: util,
            };
            cols.forEach((col) => doc.text(String(rowData[col.key]), col.x, y, { align: col.align }));
            y += rowH;
        });

        // Footer
        doc.setDrawColor(220);
        doc.line(40, 570, w - 40, 570);
        doc.setFontSize(7);
        doc.setTextColor(140);
        doc.text("VAULT · Credit Card Management · never store CVV / OTP / PIN / net-banking passwords", 40, 583);
        const pageCount = doc.internal.getNumberOfPages();
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, w - 40, 583, { align: "right" });

        const fname = `nexus-cards-${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(fname);
        toast.success(`Downloaded ${fname}`);
    };

    const filtered = useMemo(() => {
        return cards.filter((c) => {
            if (status !== "all" && c.status !== status) return false;
            if (kind !== "all" && (c.card_kind || "Credit Card") !== kind) return false;
            if (q) {
                const needle = q.toLowerCase();
                return (
                    c.bank_name?.toLowerCase().includes(needle) ||
                    c.card_name?.toLowerCase().includes(needle) ||
                    c.card_holder_name?.toLowerCase().includes(needle) ||
                    c.last_4?.includes(q)
                );
            }
            return true;
        });
    }, [cards, q, status, kind]);

    return (
        <div data-testid="cards-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Credit Card Master</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        All cards.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">
                        {cards.length} cards on file · {filtered.length} shown
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {hasRole("admin", "finance") && cards.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={printCards}
                            data-testid="cards-print-btn"
                            className="border-stone-200 hover:border-stone-900"
                        >
                            <Printer size={15} className="mr-1.5" /> Print / PDF
                        </Button>
                    )}
                    {hasRole("admin", "finance") && (
                        <Link to="/cards/new">
                            <Button data-testid="add-card-btn" className="bg-stone-900 hover:bg-stone-800 text-white">
                                <Plus size={16} className="mr-1.5" /> Add card
                            </Button>
                        </Link>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                        placeholder="Search by bank, card, holder or last 4…"
                        className="pl-9"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="cards-search-input"
                    />
                </div>
                <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger className="w-[170px]" data-testid="cards-kind-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All card types</SelectItem>
                        {CARD_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[180px]" data-testid="cards-status-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                        <SelectItem value="Blocked">Blocked</SelectItem>
                        <SelectItem value="Lost">Lost</SelectItem>
                        <SelectItem value="Replaced">Replaced</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Bank</th>
                                <th>Card</th>
                                <th>Last 4</th>
                                <th>Holder</th>
                                <th className="text-right">Limit</th>
                                <th>Bill Date</th>
                                <th>Due Date</th>
                                <th className="text-right">Outstanding</th>
                                <th>Status</th>
                                <th>Due</th>
                                <th>Health · Risk</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => {
                                const os = outstanding[c.id] || 0;
                                const dueStatus = computeDueStatus(c, os);
                                const s = scores[c.id];
                                const healthTone = !s
                                    ? "text-stone-400 border-stone-200 bg-stone-50"
                                    : s.health_score >= 90
                                      ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                                      : s.health_score >= 70
                                        ? "text-blue-700 border-blue-200 bg-blue-50"
                                        : s.health_score >= 50
                                          ? "text-amber-700 border-amber-200 bg-amber-50"
                                          : "text-red-700 border-red-200 bg-red-50";
                                const riskTone = !s
                                    ? "text-stone-400 border-stone-200 bg-stone-50"
                                    : s.risk_score >= 75
                                      ? "text-red-700 border-red-200 bg-red-50"
                                      : s.risk_score >= 50
                                        ? "text-amber-700 border-amber-200 bg-amber-50"
                                        : s.risk_score >= 25
                                          ? "text-blue-700 border-blue-200 bg-blue-50"
                                          : "text-emerald-700 border-emerald-200 bg-emerald-50";
                                return (
                                    <tr key={c.id} data-testid={`card-row-${c.id}`}>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="w-2 h-8 rounded-sm"
                                                    style={{ background: c.color_tag || "#292524" }}
                                                />
                                                <span className="font-medium text-stone-900">{c.bank_name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <Link
                                                    to={`/cards/${c.id}`}
                                                    className="hover:underline text-stone-900"
                                                >
                                                    {c.card_name}
                                                </Link>
                                                <span
                                                    className={`inline-flex items-center text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${kindPillClass(c.card_kind)}`}
                                                    data-testid={`card-kind-pill-${c.id}`}
                                                    title="Card type"
                                                >
                                                    {shortKind(c.card_kind)}
                                                </span>
                                                {(() => {
                                                    const rv = rewardValue[c.id];
                                                    if (!rv || (rv.rewards_earned_ytd === 0 && rv.annual_fee_paid === 0)) return null;
                                                    const net = rv.net_benefit;
                                                    const pos = net >= 0;
                                                    return (
                                                        <Link
                                                            to={`/cards/${c.id}/view`}
                                                            title={`FY net: earned ${fmtMoney(rv.rewards_earned_ytd)} − fees ${fmtMoney(rv.annual_fee_paid)}${rv.annual_fee_waived ? " (waived)" : ""}`}
                                                            className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border tabular-nums ${pos ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-red-700 border-red-200 bg-red-50"}`}
                                                            data-testid={`card-net-benefit-${c.id}`}
                                                        >
                                                            {pos ? "+" : "−"}{fmtMoney(Math.abs(net))}
                                                        </Link>
                                                    );
                                                })()}
                                            </div>
                                            <div className="text-[11px] text-stone-500">
                                                {c.card_network} · {c.category}
                                                {c.card_level ? ` · ${c.card_level}` : ""}
                                            </div>
                                        </td>
                                        <td className="font-mono-figure">
                                            <div className="flex items-center gap-1.5">
                                                <Sensitive level="highly_sensitive" as="span" data-testid={`card-pan-${c.id}`}>•••• {c.last_4}</Sensitive>
                                                {c.has_statement_password && (
                                                    <span
                                                        className="text-stone-400"
                                                        title="Statement PDF password saved"
                                                        aria-label="Statement password saved"
                                                    >
                                                        <Lock size={10} />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{c.card_holder_name || "—"}</td>
                                        <td className="text-right tabular-nums">{fmtMoney(c.credit_limit, c.currency)}</td>
                                        <td className="tabular-nums">{c.billing_end_day || "—"}</td>
                                        <td className="tabular-nums">{c.payment_due_day || "—"}</td>
                                        <td className={`text-right tabular-nums font-medium ${os > 0 ? "text-stone-900" : "text-stone-400"}`}>
                                            {fmtMoney(os, c.currency)}
                                        </td>
                                        <td><StatusPill status={c.status} /></td>
                                        <td><StatusPill status={dueStatus} /></td>
                                        <td>
                                            {s ? (
                                                <div
                                                    className="flex items-center gap-1.5"
                                                    title={s.factors?.length ? s.factors.join(" · ") : "All clear"}
                                                    data-testid={`card-score-${c.id}`}
                                                >
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-medium ${healthTone}`}>
                                                        <span className="tabular-nums">{s.health_score}</span>
                                                        <span className="opacity-70">{s.health_label}</span>
                                                    </span>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-medium ${riskTone}`}>
                                                        {s.risk_label}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-stone-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1">
                                            <Link to={`/cards/${c.id}/view`} title="360° view" className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" data-testid={`card-view-${c.id}`}>
                                                    <Eye size={13} />
                                                </Link>
                                                {hasRole("admin", "finance", "expense_user") && (
                                                    <Link to={`/quick-entry?card=${c.id}`} title="Add expense" className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" data-testid={`card-expense-${c.id}`}>
                                                        <Receipt size={13} />
                                                    </Link>
                                                )}
                                                {hasRole("admin", "finance") && (
                                                    <>
                                                        <Link to={`/statements?card=${c.id}`} title="Upload statement" className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" data-testid={`card-stmt-${c.id}`}>
                                                            <Upload size={13} />
                                                        </Link>
                                                        <Link to={`/payments?card=${c.id}`} title="Record payment" className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" data-testid={`card-pay-${c.id}`}>
                                                            <Banknote size={13} />
                                                        </Link>
                                                    </>
                                                )}
                                                {hasRole("admin") && c.status !== "Closed" && (
                                                    <button onClick={() => archive(c.id, `${c.bank_name} ${c.last_4}`)} title="Archive" className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" data-testid={`card-archive-${c.id}`}>
                                                        <Archive size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={12} className="p-0">
                                        <EmptyState
                                            icon={CreditCard}
                                            title="Your cards, all in one place."
                                            hint="Add your first credit card to start tracking spend, statements, dues and rewards."
                                            cta={hasRole("admin", "finance") ? { to: "/cards/new", label: "Add a card", testId: "empty-cards-cta" } : null}
                                            demoTo="/cards"
                                            className="rounded-none border-0 py-12"
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
